/* eslint-disable @typescript-eslint/no-unused-vars */
import queryString from 'query-string'
import { useState } from 'react'
import useSWR, { mutate } from 'swr'

import { stringifyParams } from 'utils/stringFormatting'

import * as T from 'types'

import { api } from '../api'
import { fetchWithToken } from '../fetchers'
import { getDataFromResponse } from './utils/dataHelpers'
import { getFetchErrorMessage, getUpdateErrorMessage } from './utils/errorMessageHelpers'

export const useRoutingRule = (id: string = '') => {
  const apiEndpoint: string | null = id ? `/routingRule/${id}` : null

  const response = useSWR<T.IRoutingRule | T.IServerError>(apiEndpoint, fetchWithToken)

  let routingRule: T.IRoutingRule | undefined

  if (response.data) routingRule = response.data as T.IRoutingRule

  const [errors, setErrors] = useState<{ [key: string]: T.IServerError }>({})
  const error = getFetchErrorMessage(response, 'routingRule')

  const update = async (updates: Partial<T.IRoutingRule>) => {
    if (!routingRule || !id) {
      return setErrors({ ...errors, update: { message: 'There is no routing rule' } })
    }

    const updateResponse = await api.updateRoutingRule(routingRule._id, updates)
    const updatedRoutingRule = getDataFromResponse<T.IRoutingRule>(updateResponse)

    const userReadableError = 'failed to update'
    const updateError = getUpdateErrorMessage(updateResponse, { userReadableError })

    if (updateError.message) return setErrors({ ...errors, update: updateError })

    await mutate(apiEndpoint, updatedRoutingRule, false)
  }

  const toggleActive = async () => {
    if (!routingRule || !id) {
      return setErrors({ ...errors, toggleActive: { message: 'There is no routing rule' } })
    }

    const toggleResponse = await api.toggleRoutingRuleActive(routingRule._id)
    const updatedRoutingRule = getDataFromResponse<T.IRoutingRule>(toggleResponse)

    const userReadableError = 'failed to toggle active status'
    const toggleError = getUpdateErrorMessage(toggleResponse, { userReadableError })

    if (toggleError.message) return setErrors({ ...errors, toggleActive: toggleError })

    await mutate(apiEndpoint, updatedRoutingRule, false)
    await mutate('/routingRule/search')
  }

  const deleteRule = async () => {
    if (!routingRule || !id) {
      return setErrors({ ...errors, delete: { message: 'There is no routing rule' } })
    }

    const deleteResponse = await api.deleteRoutingRule(routingRule._id)

    const userReadableError = 'failed to delete'
    const deleteError = getUpdateErrorMessage(deleteResponse, { userReadableError })

    if (deleteError.message) return setErrors({ ...errors, delete: deleteError })

    await mutate('/routingRule/search')
  }

  return {
    ...response,
    routingRule,
    update,
    toggleActive,
    deleteRule,
    error,
    errors,
  }
}
